import { useState } from "react";
import { NumField, PrimaryButton, ResultRow, Section, SaveButton } from "@/components/CalcUI";
import { killSheet, downloadKillSheetPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FileDown, FolderOpen } from "lucide-react";
import TemplatePicker from "@/components/TemplatePicker";
import { toast } from "sonner";

export default function KillSheet() {
  const { isAuthed, hasPro, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const [inp, setInp] = useState({
    tvd: "10000",
    current_mw: "10.0",
    sidpp: "500",
    sicp: "700",
    scr: "900",
    hole_id: "8.5",
    dp_od: "5.0",
    dp_id: "4.276",
    dp_length: "9500",
    dc_od: "6.5",
    dc_id: "2.813",
    dc_length: "500",
    pump_output: "0.117",
    safety_factor: "0.3",
    lot_ppg: "14.5",
    shoe_tvd: "4500",
  });
  const [out, setOut] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  const run = async () => {
    try {
      const parsed = Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)]));
      // Kill sheet inputs/labels are field units only (MAASP, ICP and step-down use field formulas).
      const r = await killSheet({ ...parsed, units: "field" });
      setOut(r);
    } catch (e) {
      toast.error("Check inputs — kill sheet failed");
    }
  };

  const field = (k, label, unit) => (
    <NumField testid={`ks-${k}`} label={label} unit={unit} value={inp[k]} onChange={(v) => setInp({ ...inp, [k]: v })} />
  );

  return (
    <div data-testid="kill-sheet-page">
      <Section
        title="Kill Sheet (Wait & Weight)"
        subtitle="Full pre-recorded kill sheet: dynamic well data, string geometry, slow circulating rate. Computes KMW, ICP/FCP, strokes to bit / surface, pressure step-down, and MAASP."
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div className="border border-[#334155] bg-[#11151c]">
            <div className="px-5 py-3 border-b border-[#334155] flex items-center justify-between">
              <h3 className="heading text-sm font-bold uppercase tracking-wide">Dynamic Well Data</h3>
              <span data-testid="ks-field-units-badge" className="text-[10px] mono text-[#94a3b8]">SHUT-IN VALUES · FIELD UNITS</span>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {field("tvd", "TVD", "ft")}
              {field("current_mw", "Current MW", "ppg")}
              {field("sidpp", "SIDPP", "psi")}
              {field("sicp", "SICP", "psi")}
              {field("scr", "SCR pressure", "psi")}
              {field("pump_output", "Pump output", "bbl/stk")}
              {field("safety_factor", "Safety factor", "ppg")}
              {field("lot_ppg", "LOT", "ppg")}
              {field("shoe_tvd", "Shoe TVD", "ft")}
            </div>
          </div>

          <div className="border border-[#334155] bg-[#11151c]">
            <div className="px-5 py-3 border-b border-[#334155]">
              <h3 className="heading text-sm font-bold uppercase tracking-wide">String Geometry</h3>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {field("hole_id", "Hole/Csg ID", "in")}
              <div />
              {field("dp_od", "DP OD", "in")}
              {field("dp_id", "DP ID", "in")}
              {field("dp_length", "DP length", "ft")}
              <div />
              {field("dc_od", "DC OD", "in")}
              {field("dc_id", "DC ID", "in")}
              {field("dc_length", "DC length", "ft")}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-between items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <PrimaryButton testid="ks-run" onClick={run}>Compute Kill Sheet</PrimaryButton>
            <button
              data-testid="ks-tpl-btn"
              onClick={() => setTplOpen(true)}
              className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors"
            >
              <FolderOpen className="w-3.5 h-3.5" /> Templates
            </button>
          </div>
          <div className="flex items-center gap-2">
            {out && (
              <button
                data-testid="ks-pdf-btn"
                onClick={async () => {
                  if (!isAuthed) return setShowAuth(true);
                  if (!hasPro) return navigate("/pricing");
                  try {
                    await downloadKillSheetPdf("Kill Sheet", inp, out);
                    toast.success("PDF downloaded");
                  } catch {
                    toast.error("PDF failed");
                  }
                }}
                className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors"
              >
                <FileDown className="w-3.5 h-3.5" /> PDF
              </button>
            )}
            <SaveButton getPayload={() => out && { category: "Kill Sheet", calculator: "kill_sheet", inputs: inp, outputs: out, units: "field", default_name: "Kill Sheet" }} />
          </div>
        </div>

        <TemplatePicker
          open={tplOpen}
          onClose={() => setTplOpen(false)}
          calculator="kill_sheet"
          onApply={(tpl) => {
            setInp({ ...inp, ...tpl.inputs });
            toast.success(`Loaded: ${tpl.name}`);
          }}
        />

        {out && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="border border-[#334155] bg-[#11151c] p-5">
              <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-3">Pressures</h4>
              <ResultRow label="KMW" value={out.kmw?.kmw} unit={out.kmw?.unit} emphasis />
              <ResultRow label="ICP" value={out.icp?.icp} unit="psi" />
              <ResultRow label="FCP" value={out.fcp?.fcp} unit="psi" />
              <ResultRow label="Pressure step" value={out.pressure_step_psi_per_stk} unit="psi/stk" />
              <ResultRow label="MAASP" value={out.maasp?.maasp} unit="psi" />
              <ResultRow label="Formation P" value={out.formation_pressure?.fp_psi} unit="psi" />
            </div>
            <div className="border border-[#334155] bg-[#11151c] p-5">
              <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-3">Volumes</h4>
              <ResultRow label="Surface → Bit" value={out.surface_to_bit_bbl} unit="bbl" />
              <ResultRow label="Bit → Surface" value={out.bit_to_surface_bbl} unit="bbl" />
              <ResultRow label="Total circ" value={out.total_circulation_bbl} unit="bbl" emphasis />
            </div>
            <div className="border border-[#334155] bg-[#11151c] p-5">
              <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-3">Strokes</h4>
              <ResultRow label="To bit" value={out.strokes_to_bit?.strokes} unit="stk" emphasis />
              <ResultRow label="Bit → Surface" value={out.strokes_bit_to_surface?.strokes} unit="stk" />
              <ResultRow label="Total" value={out.strokes_total?.strokes} unit="stk" />
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
